import type { AccountView, JsonAccountStats } from './types';

/** Flattens the per-exchange `Accounts` stats into one {@link AccountView} row per account. */
export const prepareAccounts = (accounts: JsonAccountStats[]): AccountView[] =>
  accounts.flatMap((item, idx) =>
    item.accounts.map((account) => ({
      name: String(account.id),
      label: account.name,
      status: item.status ?? 0,
      message: item.error ?? '',
      proxy: '',
      inviteLink: account.inviteLink ?? '',
      group: idx + 1,
      exchange: item.exchange,
      botType: account.botTypes[0] || undefined,
      ticker: account.ticker || undefined,
    })),
  );

const sortFunction = (a: AccountView, b: AccountView, sortKey: string) => {
  switch (sortKey) {
    case 'exchange:asc':
      return a.name.localeCompare(b.name);
    case 'exchange:desc':
      return b.name.localeCompare(a.name);
    default:
      return 0;
  }
};

const filterFunction = (item: AccountView, searchValueDefault: string) => {
  const parts = searchValueDefault.toLowerCase().split(' ');
  const itemString = `${item.name} ${item.label} `.toLowerCase();
  return parts.every((token) => itemString.includes(token));
};

/**
 * Filters accounts by a search string (applied from 3 characters, every word must match the name
 * or label) and sorts them by `sortKey` (`exchange:asc` / `exchange:desc`).
 */
export const getAccountsFilteredSortedData = (
  accounts: AccountView[],
  sortKey: string,
  searchValue: string,
) => {
  const filterData = (accounts || []).filter((pair) => {
    if (searchValue.length > 2 && !filterFunction(pair, searchValue)) {
      return false;
    }

    return true;
  });

  return filterData.toSorted((a, b) => sortFunction(a, b, sortKey));
};

/** Maps every account id to its `[base, quote]` tokens, parsed from the `BASE/QUOTE` ticker. */
export const tokensByAccounts = (stats: JsonAccountStats[] = []) =>
  stats.reduce<Record<string, [string, string]>>((acc, { accounts }) => {
    accounts.forEach(({ id, ticker }) => {
      const [token1, token2] = (ticker || '').split('/');
      acc[id] = [token1, token2];
    });
    return acc;
  }, {});
